import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';

type RevealAnimation = {
  delay: number;
  duration?: number;
  selector: string;
  toVars?: Record<string, string | number>;
};

interface Options {
  animateTitle?: boolean;
  revealAnimations: RevealAnimation[];
  scopeRef: RefObject<HTMLElement | null>;
  splitType?: string | null;
  titleFromVars?: Record<string, string | number>;
  titleSelector?: string;
  titleSetVars?: Record<string, string | number>;
}

export const useHeroIntroAnimation = ({
  animateTitle = true,
  revealAnimations,
  scopeRef,
  splitType = 'words,chars',
  titleFromVars,
  titleSelector = '#hero-title',
  titleSetVars,
}: Options) => {
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
  const revealAnimationsRef = useRef(revealAnimations);
  const splitTypeRef = useRef(splitType);
  const titleFromVarsRef = useRef(titleFromVars);
  const titleSelectorRef = useRef(titleSelector);
  const titleSetVarsRef = useRef(titleSetVars);

  useIsomorphicLayoutEffect(() => {
    let mounted = true;
    let cleanup: (() => void) | undefined;

    const runAnimation = async () => {
      const titleElement = scopeRef.current?.querySelector(
        titleSelectorRef.current,
      ) as HTMLElement | null;

      if (titleElement) {
        titleElement.style.visibility = 'hidden';
      }

      if (typeof document !== 'undefined' && 'fonts' in document) {
        await (document as Document & { fonts: FontFaceSet }).fonts.ready;
      }

      const [{ default: gsap }, splitTextModule] = await Promise.all([
        import('gsap/dist/gsap'),
        animateTitle ? import('gsap/dist/SplitText') : Promise.resolve(null),
      ]);

      if (!mounted || !scopeRef.current) {
        return;
      }

      const q = gsap.utils.selector(scopeRef);
      const timelines = revealAnimationsRef.current
        .map(({ delay, duration = 0.8, selector, toVars = {} }) => {
          const targets = q(selector);

          if (!targets.length) {
            return null;
          }

          return gsap.timeline({ delay }).to(targets, { duration, ...toVars });
        })
        .filter(Boolean);

      if (!animateTitle) {
        cleanup = () => {
          timelines.forEach(timeline => timeline?.kill());
        };
        return;
      }

      const SplitText = splitTextModule?.default;
      if (!SplitText) {
        cleanup = () => {
          timelines.forEach(timeline => timeline?.kill());
        };
        return;
      }

      gsap.registerPlugin(SplitText);

      const [scopedTitleElement] = q(titleSelectorRef.current) as HTMLElement[];
      if (!scopedTitleElement) {
        cleanup = () => {
          timelines.forEach(timeline => timeline?.kill());
        };
        return;
      }

      const titleTimeline = gsap.timeline();
      let revertSplit: (() => void) | undefined;

      if (splitTypeRef.current) {
        const splitText = new SplitText(scopedTitleElement, { type: splitTypeRef.current });

        gsap.set(scopedTitleElement, {
          perspective: 400,
          opacity: 1,
          visibility: 'visible',
          ...titleSetVarsRef.current,
        });
        titleTimeline.fromTo(
          splitText.chars,
          {
            opacity: 0,
            y: 80,
            rotationX: 90,
            transformOrigin: '0% 50% -50',
            ...titleFromVarsRef.current,
          },
          {
            delay: 0.8,
            duration: 1,
            opacity: 1,
            y: 0,
            rotationX: 0,
            ease: 'power3.out',
          },
        );

        revertSplit = () => splitText.revert();
      } else {
        titleTimeline.fromTo(
          scopedTitleElement,
          {
            opacity: 0,
            visibility: 'visible',
            y: 24,
            ...titleFromVarsRef.current,
          },
          {
            delay: 0.8,
            duration: 0.9,
            opacity: 1,
            y: 0,
            ease: 'power3.out',
            ...titleSetVarsRef.current,
          },
        );
      }

      cleanup = () => {
        titleTimeline.kill();
        revertSplit?.();
        timelines.forEach(timeline => timeline?.kill());
      };
    };

    runAnimation();

    return () => {
      mounted = false;
      cleanup?.();
    };
  }, [scopeRef]);
};
